/**
 * useTableUrlPersistence
 *
 * A reusable hook that adds URL-based filter persistence to any table page.
 *
 * ── What it does ─────────────────────────────────────────────────────────────
 * 1. Initialises page / pageSize / search / sorting state from URL params so
 *    a browser refresh restores the exact table state the user left.
 * 2. On first load with an empty URL, fetches the user's last saved preferences
 *    from the backend (GET /view-preferences/{viewName}) and applies them.
 * 3. Keeps the URL in sync with current state using `navigate({ replace: true })`
 *    so the URL always reflects what the table is showing.
 * 4. Auto-saves preferences to the backend (POST /view-preferences/{viewName})
 *    after a debounce whenever the table state changes.
 *
 * ── Usage ────────────────────────────────────────────────────────────────────
 *
 *   // 1. Add validateSearch to the route:
 *   export const Route = createFileRoute("/_authenticated/my-page/")({
 *     component: MyPage,
 *     validateSearch: tableSearchSchema.parse,
 *   });
 *
 *   // 2. In your component:
 *   const navigate = useNavigate();
 *   const searchParams = useSearch({ from: "/_authenticated/my-page/" });
 *
 *   const { page, setPage, pageSize, setPageSize, searchValue, setSearchValue,
 *           sorting, setSorting, debouncedSearch, filters, filtersKey } =
 *     useTableUrlPersistence({
 *       viewName: "myPageList",
 *       filterKeyToLabel: MY_PAGE_FILTER_KEY_TO_LABEL,
 *       searchParams,
 *       navigate,
 *       tableColumns,
 *       getSortByField,
 *     });
 *
 *   // 3. Pass into buildTableQueryOptions as before — nothing else changes.
 *
 * ── filterKeyToLabel ─────────────────────────────────────────────────────────
 * A Record that maps the *backend* filterBy key (e.g. "status") to the column
 * label used as the Zustand filter-store key (e.g. "Status"). Must stay in
 * sync with the searchParams.filterByKey values in the page's tableColumns.
 */

import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useDebouncedValue } from "@mantine/hooks";
import type { SortingState } from "@tanstack/react-table";
import type { Dispatch, SetStateAction } from "react";
import { z } from "zod";
import { useTableFilterBaseStore, useTableFilterStore } from "@/store/tableFilterStore";
import type { TColumn } from "@/components/ui/TableCard/TableCard";
import type { FilterValue } from "@/store/tableFilterStore";
import { useGetViewPreferences, useSaveViewPreferences } from "@/hooks/useViewPreferences";
import { DEBOUNCED_WAIT_TIME } from "@/components/data/constants";
import { useFilterBarStore } from "@/store/filterBarStore";

// ── Shared URL schema (use in every table route's validateSearch) ─────────────
export const tableSearchSchema = z.object({
  page: z.coerce.number().int().positive().optional().catch(undefined),
  limit: z.coerce.number().int().positive().optional().catch(undefined),
  search: z.string().optional().catch(undefined),
  sortBy: z.string().optional().catch(undefined),
  sortOrder: z.enum(["asc", "desc"]).optional().catch(undefined),
  filterBy: z.string().optional().catch(undefined),
});

export type TableUrlSearchParams = z.infer<typeof tableSearchSchema>;

// ── Hook options ──────────────────────────────────────────────────────────────
export type UseTableUrlPersistenceOptions = {
  /** Identifier sent to the view-preferences API (e.g. "locationList") */
  viewName: string;
  /**
   * Maps the backend filterBy key (searchParams.filterByKey in tableColumns)
   * to the column label that is used as the Zustand filter-store key.
   * Example: { status: "Status", clientName: "Client Name" }
   */
  filterKeyToLabel: Record<string, string>;
  /** Result of useSearch() in the parent component */
  searchParams: TableUrlSearchParams;
  /** Result of useNavigate() in the parent component */

  navigate: (opts: any) => void;
  /** Current tableColumns state from the parent component */
  tableColumns: TColumn[];
  /**
   * When false, disables URL read/write and filter reset on mount.
   * Useful for tabbed pages where only the active tab should own the URL.
   * Backend preference save/load still works regardless.
   * Defaults to true.
   */
  enableUrlSync?: boolean;
};

// ── Hook return value ─────────────────────────────────────────────────────────
export type UseTableUrlPersistenceReturn = {
  /** The viewName passed to this hook — forward to tableCardProps so ApiFilterChip
   *  writes to the same filter namespace that this hook reads from. */
  viewName: string;
  /** Commit the current staged (chip-selected) filters and trigger an API fetch. */
  applyFilters: () => void;
  /** Clear all filters and reset pagination to page=1, limit=10. */
  resetFilters: () => void;
  page: number;
  setPage: Dispatch<SetStateAction<number>>;
  pageSize: number;
  setPageSize: Dispatch<SetStateAction<number>>;
  searchValue: string;
  setSearchValue: Dispatch<SetStateAction<string>>;
  sorting: SortingState;
  setSorting: Dispatch<SetStateAction<SortingState>>;
  debouncedSearch: string;
  filters: Record<string, FilterValue[]>;
  filtersKey: string;
  /** True while GET view-preferences is loading OR while a save mutation is in-flight.
   *  Pass this into the table's isLoading prop so the table shows a loading overlay
   *  during preference fetch/save. */
  isPrefsLoading: boolean;
  /** True once the GET view-preferences request has settled (success or error).
   *  Use this to gate the list query so the first API call fires only after
   *  saved preferences have been applied — preventing a double API call on mount. */
  isPrefsReady: boolean;
};

// ── Helper ────────────────────────────────────────────────────────────────────
// Each stored filter entry is either:
//   - new format: { id: string; value: string } — serialised from FilterValue so
//     both the backend ID *and* the display label survive a page refresh.
//   - old format (backwards-compat): plain string — treated as id === value.
type StoredFilterEntry = string | { id: string; value: string; label?: string };

function storedEntryToFilterValue(entry: StoredFilterEntry): FilterValue {
  if (typeof entry === "string") {
    // Legacy format: the stored string was `fv.value` (the display label).
    // id and value are both set to that string so existing saved preferences
    // continue to work — they just lose the distinction between id and label.
    return { id: entry, value: entry, label: entry };
  }
  return { id: entry.id, value: entry.value, label: entry.label ?? entry.value };
}

function restoreFiltersFromString(
  filterByJson: string,
  filterKeyToLabel: Record<string, string>,
  addFilter: (key: string, value: FilterValue) => void,
) {
  try {
    const filterObj = JSON.parse(filterByJson) as Record<string, StoredFilterEntry[]>;
    Object.entries(filterObj).forEach(([filterByKey, entries]) => {
      const colLabel = filterKeyToLabel[filterByKey];
      if (!colLabel) return;
      entries.forEach((entry) => {
        addFilter(colLabel, storedEntryToFilterValue(entry));
      });
    });
  } catch {
    // Ignore malformed filterBy JSON
  }
}

// Like restoreFiltersFromString but returns the committed-filter map instead of
// writing to the Zustand store. Used when restoring from URL / preferences so
// that the committed state (which drives API calls) is also initialised.
function parseFilterByToCommitted(
  filterByJson: string,
  filterKeyToLabel: Record<string, string>,
): Record<string, FilterValue[]> {
  try {
    const filterObj = JSON.parse(filterByJson) as Record<string, StoredFilterEntry[]>;
    const result: Record<string, FilterValue[]> = {};
    Object.entries(filterObj).forEach(([filterByKey, entries]) => {
      const colLabel = filterKeyToLabel[filterByKey];
      if (!colLabel) return;
      result[colLabel] = entries.map(storedEntryToFilterValue);
    });
    return result;
  } catch {
    return {};
  }
}

// ── Hook ──────────────────────────────────────────────────────────────────────
export function useTableUrlPersistence({
  viewName,
  filterKeyToLabel,
  searchParams,
  navigate,
  tableColumns,
  enableUrlSync = true,
}: UseTableUrlPersistenceOptions): UseTableUrlPersistenceReturn {
  // ── Managed state ───────────────────────────────────────────────────────────
  const [page, setPage] = useState(() => (enableUrlSync ? (searchParams.page ?? 1) : 1));
  const [pageSize, setPageSize] = useState(() =>
    enableUrlSync ? (searchParams.limit ?? 100) : 100,
  );
  const [searchValue, setSearchValue] = useState(() =>
    enableUrlSync ? (searchParams.search ?? "") : "",
  );
  const [debouncedSearch] = useDebouncedValue(searchValue, DEBOUNCED_WAIT_TIME);

  const [sorting, setSorting] = useState<SortingState>(() => {
    if (enableUrlSync && searchParams.sortBy) {
      return [{ id: searchParams.sortBy, desc: searchParams.sortOrder === "desc" }];
    }
    return [];
  });

  // ── Zustand filter store ────────────────────────────────────────────────────
  const resetFiltersBase = useTableFilterBaseStore((s) => s.resetFilters);
  const addFilterBase = useTableFilterBaseStore((s) => s.addFilter);

  // liveFilters: what the user has staged via chips (updates immediately on chip
  // interaction). Used only for chip display — does NOT drive API calls.
  const { filters: liveFilters } = useTableFilterStore(viewName, "all");

  // committedFilters: the filter state that actually drives API calls and URL
  // sync. Only updates when the user clicks "Apply Filter" or "Reset Filter",
  // or when filters are restored from a saved URL / backend preferences.
  const [committedFilters, setCommittedFilters] = useState<Record<string, FilterValue[]>>({});
  const committedFiltersKey = JSON.stringify(committedFilters);

  // Expose committedFilters as `filters` and `filtersKey` so that all downstream
  // query builders and URL-sync logic continue to work unchanged.
  const filters = committedFilters;
  const filtersKey = committedFiltersKey;

  const { mutate: savePreferences } = useSaveViewPreferences(viewName);
  const setFilterBarVisible = useFilterBarStore((s) => s.setVisible);

  // Apply: snapshot staged chips → committed state → triggers API + URL sync
  const applyFilters = useCallback(() => {
    const snapshot: Record<string, FilterValue[]> = {};
    Object.entries(liveFilters).forEach(([key, values]) => {
      if (values.length > 0) snapshot[key] = values;
    });
    setCommittedFilters(snapshot);
    setPage(1);
  }, [liveFilters]);

  // Reset: clear Zustand store for this view + committed state + sorting + pagination,
  // then immediately persist the cleared state to the backend.
  const resetFilters = useCallback(() => {
    resetFiltersBase(viewName);
    setCommittedFilters({});
    setSorting([]);
    setPage(1);
    setPageSize(10);
    setSearchValue("");
    setFilterBarVisible(viewName, false);
    savePreferences({ queryString: "page=1&limit=10" });
  }, [resetFiltersBase, viewName, savePreferences, setSearchValue, setFilterBarVisible]);

  // ── Reset page when debounced search changes ─────────────────────────────────
  // (filter changes are handled inside applyFilters / resetFilters directly)
  useEffect(() => {
    setPage(1);
  }, [debouncedSearch]);

  // ── Filter init on mount ────────────────────────────────────────────────────
  // 1. Reset any stale filters for this view (skipped when URL sync is disabled,
  //    e.g. for inactive tabs in a tabbed layout).
  // 2. Restore filters from URL ?filterBy param (if present).
  //    Restored filters are immediately committed so the API fires with them.
  useEffect(() => {
    if (!enableUrlSync) return;

    resetFiltersBase(viewName);

    if (searchParams.filterBy) {
      const addFilter = (key: string, value: FilterValue) => addFilterBase(viewName, key, value);
      restoreFiltersFromString(searchParams.filterBy, filterKeyToLabel, addFilter);
      // Also commit so the API fires without requiring an "Apply Filter" click
      const restored = parseFilterByToCommitted(searchParams.filterBy, filterKeyToLabel);
      setCommittedFilters(restored);
      if (Object.keys(restored).length > 0) {
        setFilterBarVisible(viewName, true);
      }
    }
    // This effect intentionally runs only on mount — the closure captures the
    // initial searchParams and filterKeyToLabel values which is what we want.
  }, []);

  // ── Build URL sync params ───────────────────────────────────────────────────
  // sortBy stores the column id (not the backend field name) so that restoring
  // from the URL produces a valid SortingState that TanStack Table recognises.
  // buildTableQueryOptions calls getSortByField internally to translate the
  // column id → backend field name when making the API request.
  const urlSyncParams = useMemo(() => {
    const sortOrderValue =
      sorting.length > 0 ? (sorting[0].desc ? ("desc" as const) : ("asc" as const)) : undefined;

    // Serialize each FilterValue as { id, value } so that on restore (page refresh
    // or preference load) both the backend ID *and* the display label are available.
    // This fixes the mismatch where the list API uses fv.id (e.g. numeric 1449 for
    // woStatus) but the old code was serialising fv.value ("Created"), causing the
    // wrong value to be sent to the backend after a refresh.
    const filterByKeyValues: Record<
      string,
      Array<{ id: string; value: string; label?: string }>
    > = {};
    Object.entries(filters).forEach(([colLabel, filterValues]) => {
      if (!filterValues || filterValues.length === 0) return;
      const col = tableColumns.find((c) => c.label === colLabel);
      const filterByKey = col?.searchParams?.filterByKey;
      if (!filterByKey) return;
      filterValues.forEach((fv) => {
        filterByKeyValues[filterByKey] = [
          ...(filterByKeyValues[filterByKey] ?? []),
          { id: fv.id, value: fv.value, label: fv.label ?? fv.value },
        ];
      });
    });

    return {
      page,
      limit: pageSize,
      search: debouncedSearch || undefined,
      sortBy: sorting.length > 0 ? sorting[0].id : undefined,
      sortOrder: sortOrderValue,
      filterBy: Object.keys(filterByKeyValues).length
        ? JSON.stringify(filterByKeyValues)
        : undefined,
    };
  }, [page, pageSize, debouncedSearch, sorting, filtersKey, tableColumns]);

  const urlSyncKey = JSON.stringify(urlSyncParams);

  // ── Keep URL in sync with state ─────────────────────────────────────────────
  // Uses a merge function to preserve non-table params (e.g. `tab`) set by
  // other hooks (e.g. usePersistedTabs) on the same route.
  // enableUrlSync is included in deps so switching to an active tab immediately
  // writes the tab's current state to the URL.
  useEffect(() => {
    if (!enableUrlSync) return;

    navigate({ search: (prev: any) => ({ ...prev, ...urlSyncParams }), replace: true });
    // urlSyncKey is the stable JSON key — re-run only when params actually change
  }, [urlSyncKey, enableUrlSync]);

  // ── GET view preferences (first load) ────────────────────────────────────
  // Always fetch saved preferences from the backend on mount. Backend data takes
  // priority: if the backend has a saved queryString, it is applied (overriding any
  // stale URL params). If the backend returns nothing, the current URL / default
  // state is kept as-is (the "store" fallback).
  const {
    data: viewPreferences,
    isSuccess: prefsLoaded,
    isLoading: isGetPrefsLoading,
    isError: isPrefsError,
    isFetching: isPreferencesFetching,
  } = useGetViewPreferences({
    viewName,
    enabled: true,
  });

  // Ready once the GET prefs request has fully settled (not just resolved from
  // stale cache). With staleTime: 0 on useGetViewPreferences, isFetching stays
  // true until the network response arrives, which prevents the list query from
  // firing with empty committedFilters before the restore effect runs.
  const isPrefsReady = (prefsLoaded && !isPreferencesFetching) || isPrefsError;

  // Guard: the restore effect must only run once per mount (the initial prefs fetch).
  // Without this, every save mutation that calls setQueryData on the prefs cache would
  // create a new `viewPreferences` reference → re-trigger the restore effect → call
  // setCommittedFilters / setSorting with new object references (same values) →
  // change state → rebuild queryOptions → fire the list API a second time.
  const hasRestoredFromPrefsRef = useRef(false);

  useEffect(() => {
    if (!prefsLoaded || isPreferencesFetching || !viewPreferences?.queryString) return;
    if (hasRestoredFromPrefsRef.current) return;
    hasRestoredFromPrefsRef.current = true;

    const params = new URLSearchParams(viewPreferences.queryString);

    const savedPage = Number.parseInt(params.get("page") ?? "1", 10);
    const savedLimit = Number.parseInt(params.get("limit") ?? "10", 10);
    const savedSearch = params.get("search") ?? "";
    const savedSortBy = params.get("sortBy") ?? "";
    const savedSortOrder = params.get("sortOrder") as "asc" | "desc" | null;
    const savedFilterBy = params.get("filterBy");

    if (!Number.isNaN(savedPage)) setPage(savedPage);
    if (!Number.isNaN(savedLimit)) setPageSize(savedLimit);
    if (savedSearch) setSearchValue(savedSearch);
    if (savedSortBy) {
      setSorting([{ id: savedSortBy, desc: savedSortOrder === "desc" }]);
    }
    if (savedFilterBy) {
      const addFilter = (key: string, value: FilterValue) => addFilterBase(viewName, key, value);
      restoreFiltersFromString(savedFilterBy, filterKeyToLabel, addFilter);
      // Also commit so the API fires immediately with the restored filters
      const restored = parseFilterByToCommitted(savedFilterBy, filterKeyToLabel);
      setCommittedFilters(restored);
      if (Object.keys(restored).length > 0) {
        setFilterBarVisible(viewName, true);
      }
    }
    // filterKeyToLabel and addFilterBase are stable references
  }, [prefsLoaded, isPreferencesFetching, viewPreferences]);

  // ── POST view preferences (debounced, skip initialisation window) ────────────
  // Exclude search from the save key — search is not persisted to preferences,
  // so typing in the search box must not trigger a useSaveViewPreferences call.
  const [debouncedSaveKey] = useDebouncedValue(
    JSON.stringify({ ...urlSyncParams, search: undefined }),
    1500,
  );

  const isInitializing = useRef(true);
  useEffect(() => {
    const timer = setTimeout(() => {
      isInitializing.current = false;
    }, 2000);
    return () => clearTimeout(timer);
  }, []);

  useEffect(() => {
    if (isInitializing.current) return;
    const queryString = new URLSearchParams(
      Object.entries(urlSyncParams)
        .filter(([k, v]) => k !== "search" && v != null)
        .map(([k, v]) => [k, String(v)]),
    ).toString();
    savePreferences({ queryString });
  }, [debouncedSaveKey]);

  return {
    viewName,
    applyFilters,
    resetFilters,
    page,
    setPage,
    pageSize,
    setPageSize,
    searchValue,
    setSearchValue,
    sorting,
    setSorting,
    debouncedSearch,
    filters,
    filtersKey,
    isPrefsLoading: isGetPrefsLoading,
    isPrefsReady,
  };
}
